import React, { useState, useEffect, useRef } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Settings, Building2, FileText, Phone, Mail, Globe, Image, Upload, X, DollarSign } from 'lucide-react';

const LINE_ITEM_LABELS = {
  'service': 'Process Service',
  'rush_fee': 'Rush Fee',
  'mileage': 'Mileage (per mile)',
  'filing_fee': 'Filing Fee',
  'skip_trace': 'Skip Trace',
  'copies': 'Copies (per copy)',
  'processing_fee': 'Processing Fee (%)',
  'extra_attempt': 'Extra Attempt'
};

const DEFAULT_SYSTEM_PRICES = {
  'service': 85,
  'rush_fee': 50,
  'mileage': 0.67,
  'filing_fee': 0,
  'skip_trace': 35,
  'copies': 0.10,
  'processing_fee': 3,
  'extra_attempt': 25
};

export default function InvoiceSettingsDialog({ open, onOpenChange, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: '',
    logo_size: 'medium',
    tagline: '',
    invoice_prefix: 'SRV',
    default_payment_terms: 'net_30',
    default_terms: '',
    footer_phone: '',
    footer_email: '',
    footer_website: '',
    footer_text: '',
    default_prices: { ...DEFAULT_SYSTEM_PRICES },
    custom_line_items: []
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.getInvoiceSettings();
      setSettings({
        company_name: response.data.company_name || '',
        logo_url: response.data.logo_url || '',
        logo_size: response.data.logo_size || 'medium',
        tagline: response.data.tagline || '',
        invoice_prefix: response.data.invoice_prefix || 'SRV',
        default_payment_terms: response.data.default_payment_terms || 'net_30',
        default_terms: response.data.default_terms || '',
        footer_phone: response.data.footer_phone || '',
        footer_email: response.data.footer_email || '',
        footer_website: response.data.footer_website || '',
        footer_text: response.data.footer_text || '',
        default_prices: response.data.default_prices || { ...DEFAULT_SYSTEM_PRICES },
        custom_line_items: response.data.custom_line_items || []
      });
    } catch (error) {
      console.error('Failed to load invoice settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 1MB for Base64 storage)
    if (file.size > 1024 * 1024) {
      toast.error('Logo must be under 1MB');
      return;
    }

    setUploading(true);
    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        updateField('logo_url', base64);
        setUploading(false);
        toast.success('Logo uploaded');
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    updateField('logo_url', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateInvoiceSettings(settings);
      toast.success('Invoice settings saved');
      onSave?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updatePrice = (itemType, value) => {
    setSettings(prev => ({
      ...prev,
      default_prices: {
        ...prev.default_prices,
        [itemType]: parseFloat(value) || 0
      }
    }));
  };

  const removeCustomLineItem = (index) => {
    setSettings(prev => ({
      ...prev,
      custom_line_items: prev.custom_line_items.filter((_, i) => i !== index)
    }));
    toast.success('Custom line item removed');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Invoice Branding Settings
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Customize how your invoices look. These settings apply to all invoices.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Branding Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                <Building2 className="h-4 w-4" />
                Company Branding
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Company Name</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    placeholder="Your Company Name"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <p className="text-xs text-slate-500">Appears at top of invoice (uses profile name if blank)</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Tagline / Slogan</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => updateField('tagline', e.target.value)}
                    placeholder="Professional Process Server"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <p className="text-xs text-slate-500">Appears under company name</p>
                </div>
              </div>
              
              {/* Invoice Prefix */}
              <div className="space-y-2">
                <Label className="text-slate-300">Invoice Number Prefix</Label>
                <Input
                  value={settings.invoice_prefix}
                  onChange={(e) => updateField('invoice_prefix', e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4))}
                  placeholder="SRV"
                  maxLength={4}
                  className="w-24 bg-slate-800 border-slate-700 text-white uppercase"
                />
                <p className="text-xs text-slate-500">Up to 4 letters. Format: <span className="font-mono">{(settings.invoice_prefix || 'SRV')}{new Date().getFullYear()}00001</span></p>
              </div>
              
              {/* Default Payment Terms */}
              <div className="space-y-2">
                <Label className="text-slate-300">Default Payment Terms</Label>
                <select
                  value={settings.default_payment_terms}
                  onChange={(e) => updateField('default_payment_terms', e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm"
                >
                  <option value="due_on_receipt">Due on Receipt</option>
                  <option value="net_15">Net 15</option>
                  <option value="net_30">Net 30</option>
                </select>
                <p className="text-xs text-slate-500">Applied to new invoices by default. Can be overridden per invoice.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Logo
                </Label>
                
                {/* Logo Preview and Upload */}
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  {settings.logo_url ? (
                    <div className="relative">
                      <img 
                        src={settings.logo_url} 
                        alt="Logo preview" 
                        className="h-16 w-16 object-contain rounded border border-slate-700 bg-slate-800"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-16 flex items-center justify-center rounded border border-dashed border-slate-600 bg-slate-800/50">
                      <Image className="h-6 w-6 text-slate-500" />
                    </div>
                  )}
                  
                  {/* Upload Button and Size */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid="upload-logo-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">PNG or JPG, max 1MB. Square logos work best.</p>
                    
                    {/* Logo Size Selector */}
                    {settings.logo_url && (
                      <div className="flex items-center gap-2">
                        <Label className="text-slate-400 text-xs">Size on invoice:</Label>
                        <select
                          value={settings.logo_size}
                          onChange={(e) => updateField('logo_size', e.target.value)}
                          className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white text-xs"
                          data-testid="logo-size-select"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="xlarge">Extra Large</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Default Prices Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                <DollarSign className="h-4 w-4" />
                Default Line Item Prices
              </div>
              <p className="text-xs text-slate-500 -mt-2">Set your standard rates. These will auto-populate when adding line items to invoices.</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(LINE_ITEM_LABELS).map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-slate-400 text-xs">{label}</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                        {key === 'processing_fee' ? '' : '$'}
                      </span>
                      <Input
                        type="number"
                        step={key === 'mileage' || key === 'copies' ? '0.01' : key === 'processing_fee' ? '0.5' : '1'}
                        value={settings.default_prices?.[key] ?? DEFAULT_SYSTEM_PRICES[key]}
                        onChange={(e) => updatePrice(key, e.target.value)}
                        className={`bg-slate-800 border-slate-700 text-white text-sm h-9 ${key === 'processing_fee' ? 'pl-2 pr-6' : 'pl-6'}`}
                        data-testid={`default-price-${key}`}
                      />
                      {key === 'processing_fee' && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Saved Custom Line Items Section */}
            {settings.custom_line_items && settings.custom_line_items.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                  <FileText className="h-4 w-4" />
                  Saved Custom Line Items
                </div>
                <p className="text-xs text-slate-500 -mt-2">These custom items will appear in the line item type dropdown when creating invoices.</p>
                
                <div className="space-y-2">
                  {settings.custom_line_items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-700">
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{item.description || 'Unnamed Item'}</p>
                        <p className="text-slate-400 text-xs">
                          Default price: ${(item.default_price || 0).toFixed(2)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCustomLineItem(index)}
                        className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Default Terms Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                <FileText className="h-4 w-4" />
                Default Terms & Conditions
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Payment Terms & Policies</Label>
                <Textarea
                  value={settings.default_terms}
                  onChange={(e) => updateField('default_terms', e.target.value)}
                  placeholder={`Payment Options:
• Check payable to [Your Name]
• Venmo: @yourhandle
• Zelle: your@email.com

Late Payment Policy:
• Invoices over 30 days are subject to a 1.5% monthly finance charge
• All accounts over 60 days may be sent to collections`}
                  className="bg-slate-800 border-slate-700 text-white min-h-[150px]"
                  rows={6}
                />
                <p className="text-xs text-slate-500">This text appears on every invoice. Include payment options, overdue fees, and other terms.</p>
              </div>
            </div>

            {/* Footer Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
                Footer Contact Information
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5" />
                    Phone
                  </Label>
                  <Input
                    value={settings.footer_phone}
                    onChange={(e) => updateField('footer_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5" />
                    Email
                  </Label>
                  <Input
                    value={settings.footer_email}
                    onChange={(e) => updateField('footer_email', e.target.value)}
                    placeholder="billing@example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" />
                    Website
                  </Label>
                  <Input
                    value={settings.footer_website}
                    onChange={(e) => updateField('footer_website', e.target.value)}
                    placeholder="www.example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Additional Footer Text</Label>
                <Input
                  value={settings.footer_text}
                  onChange={(e) => updateField('footer_text', e.target.value)}
                  placeholder="Thank you for your business!"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
